// Play testers (xReceived staff only): everyone who signed up at xreceived.com/testers for the Google Play
// closed test (12 testers opted in for 14 days in a row). staff_testers(), staff_set_tester_status(),
// staff_set_tester_note(), staff_delete_tester() in supabase/migrations/20261029000100_testers.sql.
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { plain } from '../lib/errors';
import { L } from '../lib/prefs';
import { useApp } from '../lib/session';
import { DbError, rpc, sb } from '../lib/supabase';
import { Btn, Card, Empty, Field, Ic, Meter, Note, PageHead, Radios } from '../ui/kit';
import { closeModal, confirmBox, openModal } from '../ui/modal';
import { Pill, SearchBox, Tabs } from '../ui/table';
import { toast } from '../ui/toast';

export type Status = 'new' | 'invited' | 'opted_in' | 'dropped';
export type Tester = {
  id: string; name: string; email: string; phone_model: string; android_version: string; country: string; city: string | null;
  whatsapp: string | null; company: string | null; lang: 'en' | 'ar'; status: Status; note: string | null; signups: number;
  consent_at: string; invited_at: string | null; invite_sent_at: string | null; invite_sends: number; opted_in_at: string | null; dropped_at: string | null; created_at: string; updated_at: string;
};
type List = { rows: Tester[]; optin_url: string | null; can_change: boolean; can_delete: boolean };

export const GOAL = 12, DAYS = 14;
const ST: Record<Status, [string, string, string]> = {
  new: ['New', 'جديد', 'info'], invited: ['Invited', 'تمت الدعوة', 'warn'], opted_in: ['Opted in', 'انضم', 'good'], dropped: ['Dropped', 'انسحب', 'mute'],
};
const ORDER: Status[] = ['new', 'invited', 'opted_in', 'dropped'];

/** A day in Dubai as yyyy-mm-dd (the closed test counts whole days). */
export const dubaiDay = (t: number | string | Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(t));
/** dd/mm/yyyy, as every date on screen (Bilal, 28 Oct 2026). */
export const dmy = (t: number | string | Date) => dubaiDay(t).split('-').reverse().join('/');
const dayNo = (opted: string, now = Date.now()) => Math.round((Date.parse(dubaiDay(now)) - Date.parse(dubaiDay(opted))) / 86_400_000) + 1;

/** When Google's rule is met: the 12th earliest opt-in plus 14 days (only testers still opted in count). */
export function readyOn(rows: Tester[]): { need: number; day: string | null } {
  const opted = rows.filter(r => r.status === 'opted_in' && r.opted_in_at).map(r => r.opted_in_at!).sort();
  if (opted.length < GOAL) return { need: GOAL - opted.length, day: null };
  const d = new Date(Date.parse(dubaiDay(opted[GOAL - 1]!)) + DAYS * 86_400_000);
  return { need: 0, day: d.toISOString().slice(0, 10) };
}

/** CSV for Excel: UTF-8 with BOM (Arabic names), quoted cells, and no cell starting a formula. */
export function testersCsv(rows: Tester[]): string {
  const cell = (v: unknown) => {
    let s = v == null ? '' : String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return /[",\n\r;]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const head = ['Name', 'Email', 'Phone model', 'Android', 'Country', 'City', 'WhatsApp', 'Company or role', 'Language', 'Status', 'Signed up', 'Invited', 'Opted in', 'Dropped', 'Times sent', 'Note'];
  const d = (x: string | null) => (x ? dmy(x) : '');
  const lines = rows.map(r => [r.name, r.email, r.phone_model, r.android_version, r.country, r.city, r.whatsapp, r.company, r.lang, ST[r.status][0], d(r.created_at), d(r.invited_at), d(r.opted_in_at), d(r.dropped_at), r.signups, r.note].map(cell).join(','));
  return '﻿' + [head.join(','), ...lines].join('\r\n') + '\r\n';
}

async function copyText(txt: string, count: number) {
  try {
    await navigator.clipboard.writeText(txt);
    toast(L(count === 1 ? '1 email copied' : count + ' emails copied', count + ' بريد نُسخ'), { icon: 'copy' });
  } catch {
    // clipboard blocked: show the list to copy by hand
    openModal({
      title: L('Copy these emails', 'انسخ هذه العناوين'), size: 'md',
      body: <><p className="muted sm">{L('Select all and copy, then paste into Play Console › Testing › Closed testing › Testers › Email list.', 'حدّد الكل وانسخ، ثم الصق في Play Console.')}</p><textarea className="inp" rows={6} readOnly value={txt} data-autofocus onFocus={e => e.currentTarget.select()} /></>,
      foot: <Btn kind="primary" onClick={closeModal}>{L('Done', 'تم')}</Btn>,
    });
  }
}

function download(rows: Tester[]) {
  const url = URL.createObjectURL(new Blob([testersCsv(rows)], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = `xreceived-play-testers-${dubaiDay(Date.now())}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function StatusPill({ r }: { r: Tester }) {
  const [en, ar, tone] = ST[r.status];
  if (r.status !== 'opted_in' || !r.opted_in_at) return <Pill tone={tone}>{L(en, ar)}</Pill>;
  const n = dayNo(r.opted_in_at);
  return n > DAYS ? <Pill tone="good">{L(`${DAYS} days done`, `اكتملت ${DAYS} يوماً`)}</Pill> : <Pill tone="good">{L(`Day ${n} of ${DAYS}`, `اليوم ${n} من ${DAYS}`)}</Pill>;
}

const PLAY_LINK = /^https:\/\/play\.google\.com\/[A-Za-z0-9/._?=&%-]+$/;

/** Emails the opt-in link (edge function tester-invite, as the signed-in staff member). */
async function sendInvites(ids: string[]): Promise<{ sent: number; of: number }> {
  const { data, error } = await sb.functions.invoke('tester-invite', { body: { ids } });
  if (error) {
    const body = await (error as { context?: Response }).context?.json?.().catch(() => null) as { error?: string } | null;
    throw new DbError(body?.error || 'network', error.message);
  }
  return data as { sent: number; of: number };
}

/** Set one status for the chosen testers; Opted in asks for the day (Google counts from it);
 *  Invited can email the Google Play opt-in link (set once, kept for everyone). */
function StatusForm({ ids, cur, link, onSaved }: { ids: string[]; cur?: Status; link: string | null; onSaved: () => void }) {
  const [st, setSt] = useState<Status>(cur && cur !== 'opted_in' ? (cur === 'new' ? 'invited' : cur) : 'opted_in');
  const today = dubaiDay(Date.now());
  const [day, setDay] = useState(today);
  const [mail, setMail] = useState(true);
  const [url, setUrl] = useState(link || '');
  const [busy, setBusy] = useState(false);
  const badDay = st === 'opted_in' && (!day || day > today || day < '2026-10-01');
  const badUrl = st === 'invited' && mail && !PLAY_LINK.test(url.trim());
  const save = async () => {
    if (badDay || badUrl) return;
    setBusy(true);
    try {
      const n = await rpc<number>('staff_set_tester_status', { p_ids: ids, p_status: st, p_day: st === 'opted_in' ? day : null });
      if (st === 'invited' && mail) {
        if (url.trim() !== (link || '')) await rpc('staff_set_tester_link', { p_url: url.trim() });
        const r = await sendInvites(ids);
        closeModal();
        toast(r.sent === r.of
          ? L(`Opt-in email sent to ${r.sent} ${r.sent === 1 ? 'tester' : 'testers'}`, `أُرسل بريد الانضمام إلى ${r.sent}`)
          : L(`Opt-in email sent to ${r.sent} of ${r.of}. Try the others again.`, `أُرسل إلى ${r.sent} من ${r.of}. أعد المحاولة للباقين.`), r.sent === r.of ? { icon: 'mail' } : { tone: 'bad' });
      } else {
        closeModal();
        toast(L(`${n} ${n === 1 ? 'tester' : 'testers'} marked ${ST[st][0].toLowerCase()}`, `حُدّث ${n}: ${ST[st][1]}`));
      }
      onSaved();
    } catch (e) {
      const code = e instanceof DbError ? e.code : '';
      toast(code === 'not_ready' ? L('Email isn\'t set up yet. The status was saved; send the link yourself for now.', 'البريد غير مُعدّ بعد. حُفظت الحالة؛ أرسل الرابط بنفسك الآن.')
        : code === 'link_invalid' ? L('Use the link Play Console shows, starting with https://play.google.com/', 'استخدم الرابط الذي يظهر في Play Console ويبدأ بـ https://play.google.com/')
        : plain(e), { tone: 'bad' });
      setBusy(false);
      onSaved();
    }
  };
  return (
    <>
      <div className="m-body">
        <Radios<Status> name="tst" value={st} onChange={setSt} opts={[
          ['invited', L('Invited', 'تمت الدعوة'), L('Added to the Play Console email list and sent the opt-in link', 'أضيف إلى قائمة Play Console وأُرسل رابط الانضمام')],
          ['opted_in', L('Opted in', 'انضم'), L('Tapped "Become a tester" and installed the app. Day 1 of 14.', 'ضغط «Become a tester» وثبّت التطبيق. اليوم 1 من 14.')],
          ['dropped', L('Dropped', 'انسحب'), L('Left the test or removed the app', 'ترك الاختبار أو حذف التطبيق')],
          ['new', L('New', 'جديد'), L('Back to not invited yet', 'إرجاع إلى لم يُدعَ بعد')]]} />
        {st === 'invited' ? <>
          <label className="chk"><input type="checkbox" checked={mail} onChange={e => setMail(e.target.checked)} /><span className="box"><Ic n="check" /></span>{L('Email them the Google Play opt-in link', 'أرسل لهم رابط الانضمام في Google Play')}</label>
          {mail ? (
            <Field label={L('Google Play opt-in link', 'رابط الانضمام في Google Play')} htmlFor="t-link" hint={L('Play Console › Test and release › Closed testing › Testers › "Join on the web". Saved for next time.', 'Play Console › الاختبار المغلق › المختبِرون › «Join on the web». يُحفظ للمرة القادمة.')} err={badUrl && url ? L('Use the link Play Console shows, starting with https://play.google.com/', 'استخدم الرابط الذي يظهر في Play Console ويبدأ بـ https://play.google.com/') : ''}>
              <input id="t-link" className={'inp' + (badUrl && url ? ' bad' : '')} dir="ltr" placeholder="https://play.google.com/apps/testing/com.xreceived.app" value={url} onChange={e => setUrl(e.target.value)} />
            </Field>
          ) : null}
          {mail ? <Note icon="mail">{L('Each tester gets one email, in their language, from no-reply@xreceived.com. Replies come to xreceived.app@gmail.com. First add them to the Play Console email list, or the link won\'t let them in.', 'يصل كل مختبِر بريد واحد بلغته من no-reply@xreceived.com، والردود إلى xreceived.app@gmail.com. أضفهم أولاً إلى قائمة Play Console وإلا فلن يعمل الرابط.')}</Note> : null}
        </> : null}
        {st === 'opted_in' ? (
          <Field label={L('Opted in on', 'تاريخ الانضمام')} htmlFor="t-day" hint={L('Play Console › Closed testing › Testers shows who joined.', 'يظهر في Play Console من انضم.')} err={badDay ? L('Pick today or an earlier day.', 'اختر اليوم أو يوماً سابقاً.') : ''}>
            <input id="t-day" className={'inp' + (badDay ? ' bad' : '')} type="date" max={today} value={day} onChange={e => setDay(e.target.value)} />
          </Field>
        ) : null}
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn>
        <Btn kind="primary" busy={busy} disabled={badDay || badUrl} onClick={() => void save()}>{st === 'invited' && mail ? L('Save and send', 'حفظ وإرسال') : L('Save', 'حفظ')}</Btn>
      </footer>
    </>
  );
}

function Detail({ r, list, onSaved }: { r: Tester; list: List; onSaved: () => void }) {
  const [note, setNote] = useState(r.note || '');
  const [busy, setBusy] = useState(false);
  const saveNote = async () => {
    setBusy(true);
    try { await rpc('staff_set_tester_note', { p_id: r.id, p_note: note }); closeModal(); toast(L('Note saved', 'حُفظت الملاحظة')); onSaved(); }
    catch (e) { toast(plain(e), { tone: 'bad' }); setBusy(false); }
  };
  const del = () => confirmBox({
    title: L(`Remove ${r.name}?`, `حذف ${r.name}؟`), danger: true, ok: L('Remove for good', 'حذف نهائياً'),
    body: L('Use this when the tester asks to be removed. Their details are deleted and can\'t be brought back. Also remove the email from the Play Console list.', 'استخدم هذا عندما يطلب المختبِر الحذف. تُحذف بياناته نهائياً. احذف بريده من قائمة Play Console أيضاً.'),
    onOk: async () => { try { await rpc('staff_delete_tester', { p_id: r.id }); toast(L('Tester removed', 'حُذف المختبِر')); onSaved(); } catch (e) { toast(plain(e), { tone: 'bad' }); } },
  });
  const row = (k: string, v: ReactNode) => <div className="t-row"><span>{k}</span><b>{v || '—'}</b></div>;
  return (
    <>
      <div className="m-body">
        <div className="t-dl">
          {row(L('Gmail', 'Gmail'), <span dir="ltr">{r.email}</span>)}
          {row(L('Phone', 'الهاتف'), `${r.phone_model} · Android ${r.android_version === 'older' ? L('older', 'أقدم') : r.android_version}`)}
          {row(L('Where', 'المكان'), [r.city, r.country].filter(Boolean).join(', '))}
          {row('WhatsApp', r.whatsapp ? <a href={'https://wa.me/' + r.whatsapp.replace(/\D/g, '')} target="_blank" rel="noreferrer" dir="ltr">{r.whatsapp}</a> : null)}
          {row(L('Company or role', 'الشركة أو الوظيفة'), r.company)}
          {row(L('Language', 'اللغة'), r.lang === 'ar' ? 'العربية' : 'English')}
          {row(L('Status', 'الحالة'), <StatusPill r={r} />)}
          {row(L('Signed up', 'سجّل في'), dmy(r.created_at) + (r.signups > 1 ? L(` · sent ${r.signups} times`, ` · أُرسل ${r.signups} مرات`) : ''))}
          {r.invited_at ? row(L('Invited', 'دُعي'), dmy(r.invited_at)) : null}
          {r.invite_sent_at ? row(L('Opt-in email', 'بريد الانضمام'), dmy(r.invite_sent_at) + (r.invite_sends > 1 ? L(` · sent ${r.invite_sends} times`, ` · أُرسل ${r.invite_sends} مرات`) : '')) : null}
          {r.opted_in_at ? row(L('Opted in', 'انضم'), dmy(r.opted_in_at)) : null}
          {r.dropped_at ? row(L('Dropped', 'انسحب'), dmy(r.dropped_at)) : null}
        </div>
        <Field label={L('Private note', 'ملاحظة خاصة')} htmlFor="t-note" hint={L('Only staff see this.', 'يراها الفريق فقط.')}>
          <textarea id="t-note" className="inp" rows={3} maxLength={500} value={note} disabled={!list.can_change} onChange={e => setNote(e.target.value)} />
        </Field>
      </div>
      <footer className="m-foot">
        {list.can_delete ? <Btn cls="danger-ghost" icon="trash" onClick={del}>{L('Remove', 'حذف')}</Btn> : null}
        <span style={{ flex: 1 }} />
        {list.can_change ? <>
          <Btn kind="ghost" onClick={() => openModal({ title: L('Change status', 'تغيير الحالة'), sub: r.name, size: 'sm', bare: true, body: <StatusForm ids={[r.id]} cur={r.status} link={list.optin_url} onSaved={onSaved} /> })}>{L('Change status', 'تغيير الحالة')}</Btn>
          <Btn kind="primary" busy={busy} disabled={note === (r.note || '')} onClick={() => void saveNote()}>{L('Save note', 'حفظ الملاحظة')}</Btn>
        </> : <Btn kind="primary" onClick={closeModal}>{L('Close', 'إغلاق')}</Btn>}
      </footer>
    </>
  );
}

export function TestersPage() {
  const { staff } = useApp();
  const [list, setList] = useState<List | null>(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<'all' | Status>('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const reload = useCallback(async () => {
    try { setList(await rpc<List>('staff_testers')); setFailed(false); } catch { setFailed(true); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  const rows = list?.rows || [];
  const count = (s: Status) => rows.filter(r => r.status === s).length;
  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return rows.filter(r => (tab === 'all' || r.status === tab) && (!n || [r.name, r.email, r.phone_model, r.city, r.country, r.company, r.whatsapp, r.note].some(x => (x || '').toLowerCase().includes(n))));
  }, [rows, tab, q]);
  useEffect(() => { setSel(s => new Set([...s].filter(id => rows.some(r => r.id === id)))); }, [rows]);
  if (!staff) return null;
  if (failed) return <><PageHead title={L('Play testers', 'مختبرو Play')} /><Card><Empty icon="alert" title={L('Couldn\'t load the testers', 'تعذّر تحميل المختبِرين')} action={<Btn onClick={() => void reload()}>{L('Try again', 'حاول مرة أخرى')}</Btn>} /></Card></>;
  if (!list) return <div className="boot" aria-busy="true" />;

  const ready = readyOn(rows);
  const opted = count('opted_in');
  const picked = shown.filter(r => sel.has(r.id));
  const target = picked.length ? picked : shown;
  const allOn = shown.length > 0 && picked.length === shown.length;
  const toggle = (id: string) => setSel(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const done = () => { setSel(new Set()); void reload(); };
  const bulk = (ids: string[]) => openModal({ title: L('Change status', 'تغيير الحالة'), sub: L(`${ids.length} ${ids.length === 1 ? 'tester' : 'testers'}`, `${ids.length} مختبِر`), size: 'sm', bare: true, body: <StatusForm ids={ids} link={list.optin_url} onSaved={done} /> });
  const open = (r: Tester) => openModal({ title: r.name, sub: r.email, size: 'md', bare: true, body: <Detail r={r} list={list} onSaved={done} /> });
  const emails = (rs: Tester[]) => void copyText(rs.map(r => r.email).join(', '), rs.length);

  return (
    <>
      <PageHead title={L('Play testers', 'مختبرو Play')} sub={L('Google Play closed test · sign-ups from xreceived.com/testers · only xReceived staff see this page', 'اختبار Google Play المغلق · التسجيلات من xreceived.com/testers · يراها فريق xReceived فقط')}
        tools={<>
          <Btn icon="copy" disabled={!target.length} onClick={() => emails(target)}>{picked.length ? L(`Copy ${picked.length} emails`, `نسخ ${picked.length} بريد`) : L('Copy emails', 'نسخ العناوين')}</Btn>
          <Btn icon="download" disabled={!rows.length} onClick={() => download(target)}>{L('Download CSV', 'تنزيل CSV')}</Btn>
        </>} />

      <Card cls="t-goal">
        <div className="t-goal-top">
          <div>
            <span className="t-k">{L('Opted in', 'انضموا')}</span>
            <b className="t-big mono">{opted}<small> / {GOAL}</small></b>
          </div>
          <p className="t-when">{ready.day
            ? (ready.day <= dubaiDay(Date.now())
              ? <><Ic n="check" />{L('14 days done for 12 testers. You can apply for production in Play Console.', 'اكتملت 14 يوماً لـ 12 مختبِراً. يمكنك طلب النشر في Play Console.')}</>
              : <>{L('If all stay, Google\'s 14 days end on ', 'إن بقي الجميع تنتهي الأيام الـ 14 في ')}<b className="mono">{dmy(ready.day)}</b></>)
            : L(`${ready.need} more ${ready.need === 1 ? 'tester needs' : 'testers need'} to opt in. Invite a few extra: some people drop out.`, `يلزم ${ready.need} مختبِرين إضافيين. ادعُ عدداً أكبر لأن بعضهم ينسحب.`)}</p>
        </div>
        <Meter v={Math.min(opted, GOAL)} max={GOAL} tone={opted >= GOAL ? 'good' : undefined} />
        <div className="mini-kpis four">
          {ORDER.map(s => <div key={s} className={ST[s][2]}><span>{L(ST[s][0], ST[s][1])}</span><b>{count(s)}</b></div>)}
        </div>
      </Card>

      {!list.can_change ? <Note icon="eye">{L('Your staff role can see the list but not change it.', 'دورك يتيح رؤية القائمة دون تعديلها.')}</Note> : null}

      <div className="toolbar">
        <Tabs<'all' | Status> cls="grow" items={[['all', L('All', 'الكل'), rows.length], ...ORDER.map(s => [s, L(ST[s][0], ST[s][1]), count(s)] as ['all' | Status, string, number])]} cur={tab} onPick={k => { setTab(k); setSel(new Set()); }} />
        <SearchBox id="testers" value={q} onChange={setQ} ph={L('Name, email, phone or city', 'الاسم أو البريد أو الهاتف أو المدينة')} />
      </div>
      <Card cls="flush">
        {shown.length ? (
          <div className={'bulk' + (picked.length ? '' : ' idle')} role="region" aria-label={L('Select testers', 'تحديد المختبِرين')}>
            <label className="chk"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(shown.map(r => r.id)))} /><span className="box"><Ic n="check" /></span>{L('Select all shown', 'تحديد الكل')}</label>
            {picked.length ? <>
              <b>{L(`${picked.length} selected`, `${picked.length} محدد`)}</b>
              <Btn sm icon="copy" onClick={() => emails(picked)}>{L('Copy emails', 'نسخ العناوين')}</Btn>
              {list.can_change ? <Btn sm kind="primary" onClick={() => bulk(picked.map(r => r.id))}>{L('Change status', 'تغيير الحالة')}</Btn> : null}
              <Btn sm kind="ghost" onClick={() => setSel(new Set())}>{L('Clear', 'إلغاء التحديد')}</Btn>
            </> : null}
          </div>
        ) : null}
        {!rows.length ? <Empty icon="phone" title={L('No sign-ups yet', 'لا تسجيلات بعد')} body={L('Share xreceived.com/testers. Each sign-up appears here.', 'شارك xreceived.com/testers. يظهر كل تسجيل هنا.')} />
          : !shown.length ? <Empty icon="search" title={L('Nothing matches', 'لا توجد نتائج')} body={L('Try a different filter or search.', 'جرّب عامل تصفية أو بحثاً مختلفاً.')} />
          : <div className="t-wrap stackable"><table className="tbl stack t-list">
            <thead><tr>
              <th className="t-sel"><span className="vh">{L('Select', 'تحديد')}</span></th>
              <th>{L('Tester', 'المختبِر')}</th><th>{L('Phone', 'الهاتف')}</th><th>{L('Where', 'المكان')}</th><th>{L('Signed up', 'سجّل')}</th><th>{L('Status', 'الحالة')}</th>
            </tr></thead>
            <tbody>{shown.map(r => (
              <tr key={r.id} className={sel.has(r.id) ? 'on' : ''}>
                <td className="t-sel"><label className="chk solo"><input type="checkbox" checked={sel.has(r.id)} aria-label={L('Select ', 'تحديد ') + r.name} onChange={() => toggle(r.id)} /><span className="box"><Ic n="check" /></span></label></td>
                <td className="ga"><button type="button" className="t-who" onClick={() => open(r)}><b>{r.name}</b><small dir="ltr">{r.email}</small></button>{r.note ? <small className="t-note"><Ic n="doc" cls="sm" />{r.note}</small> : null}</td>
                <td className="gc">{r.phone_model}<small className="sub">Android {r.android_version === 'older' ? L('older', 'أقدم') : r.android_version}</small></td>
                <td className="gd">{[r.city, r.country].filter(Boolean).join(', ')}</td>
                <td className="mono t-date">{dmy(r.created_at)}</td>
                <td className="gb"><StatusPill r={r} /></td>
              </tr>
            ))}</tbody>
          </table></div>}
      </Card>
    </>
  );
}
